import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// 2D Solar System Scene - CSS/Canvas based
function SolarSystemScene2D({ system, selectedPlanet, onPlanetClick }) {
  const [viewMode, setViewMode] = useState('system');

  useEffect(() => {
    if (selectedPlanet && selectedPlanet.moons && selectedPlanet.moons.length > 0) {
      // Keep in system view by default
      setViewMode('system');
    } else {
      setViewMode('system');
    }
  }, [selectedPlanet]);

  if (viewMode === 'planet' && selectedPlanet && selectedPlanet.moons && selectedPlanet.moons.length > 0) {
    return (
      <div className="w-full h-full relative bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center overflow-hidden">
        <PlanetWithMoonsView planet={selectedPlanet} />
        
        {/* View toggle */}
        <div className="absolute bottom-4 right-4 space-x-2">
          <button
            onClick={() => setViewMode('system')}
            className="px-4 py-2 bg-white/5 text-foreground border border-cyan-400/20 rounded-lg hover:bg-white/10 transition-all"
            data-testid="view-system"
          >
            System View
          </button>
          <button
            className="px-4 py-2 bg-cyan-500/90 text-slate-900 rounded-lg"
            data-testid="view-planet"
          >
            Planet View ({selectedPlanet.moons.length} moons)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-gradient-to-b from-slate-950 to-slate-900 flex items-center justify-center overflow-hidden">
      {/* Stars background */}
      <div className="absolute inset-0">
        {[...Array(100)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              opacity: Math.random() * 0.5 + 0.3,
              animation: `twinkle ${Math.random() * 3 + 2}s infinite ${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Solar system */}
      <div className="relative" style={{ width: '90%', height: '90%', maxWidth: '800px', maxHeight: '800px' }}>
        {/* Star */}
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            width: '40px',
            height: '40px',
            backgroundColor: system.star_color,
            boxShadow: `0 0 40px ${system.star_color}, 0 0 80px ${system.star_color}`,
            zIndex: 10
          }}
          data-testid="star"
        />

        {/* Planets */}
        {system.planets.map((planet, idx) => {
          const orbitRadius = (planet.orbit_au * 60) + 60; // Scale factor
          const planetSize = Math.max(8, Math.min(planet.radius_km / 2000, 24));
          
          return (
            <OrbitingPlanet
              key={idx}
              planet={planet}
              orbitRadius={orbitRadius}
              planetSize={planetSize}
              isSelected={selectedPlanet?.idx === planet.idx}
              onClick={() => onPlanetClick(planet)}
            />
          );
        })}
      </div>

      {/* View mode toggle for planets with moons */}
      {selectedPlanet && selectedPlanet.moons && selectedPlanet.moons.length > 0 && (
        <div className="absolute bottom-4 right-4 space-x-2">
          <button
            className="px-4 py-2 bg-cyan-500/90 text-slate-900 rounded-lg"
            data-testid="view-system"
          >
            System View
          </button>
          <button
            onClick={() => setViewMode('planet')}
            className="px-4 py-2 bg-white/5 text-foreground border border-cyan-400/20 rounded-lg hover:bg-white/10 transition-all"
            data-testid="view-planet"
          >
            Planet View ({selectedPlanet.moons.length} moons)
          </button>
        </div>
      )}
    </div>
  );
}

// Orbiting planet component
function OrbitingPlanet({ planet, orbitRadius, planetSize, isSelected, onClick }) {
  const orbitDuration = 10 + (planet.orbit_au * 5); // Slower for outer planets
  const initialAngle = planet.initial_angle || 0;

  return (
    <>
      {/* Orbit line */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-900/30"
        style={{
          width: `${orbitRadius * 2}px`,
          height: `${orbitRadius * 2}px`
        }}
      />

      {/* Planet */}
      <motion.div
        className="absolute top-1/2 left-1/2 cursor-pointer"
        style={{
          width: `${planetSize}px`,
          height: `${planetSize}px`
        }}
        animate={{
          rotate: 360
        }}
        transition={{
          duration: orbitDuration,
          repeat: Infinity,
          ease: "linear"
        }}
        onClick={onClick}
        data-testid={`planet-orbit-${planet.idx}`}
      >
        <div
          className="rounded-full transition-all duration-200 hover:scale-125"
          style={{
            width: `${planetSize}px`,
            height: `${planetSize}px`,
            backgroundColor: planet.color,
            transform: `translateX(${orbitRadius}px)`,
            boxShadow: isSelected ? `0 0 20px ${planet.color}` : `0 0 10px ${planet.color}`,
            border: isSelected ? `2px solid #18D2FF` : 'none'
          }}
        />
      </motion.div>
    </>
  );
}

// Planet with moons view
function PlanetWithMoonsView({ planet }) {
  const planetSize = 100;

  return (
    <div className="relative" style={{ width: '90%', height: '90%', maxWidth: '600px', maxHeight: '600px' }}>
      {/* Planet */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: `${planetSize}px`,
          height: `${planetSize}px`,
          backgroundColor: planet.color,
          boxShadow: `0 0 40px ${planet.color}`,
          zIndex: 10
        }}
      />

      {/* Moons */}
      {planet.moons && planet.moons.map((moon, idx) => {
        if (!moon || typeof moon.orbit_km !== 'number' || moon.orbit_km <= 0) {
          return null;
        }

        const orbitRadius = Math.max(80, Math.min(moon.orbit_km / 2000, 250));
        const moonSize = Math.max(6, Math.min(moon.radius_km / 500, 16));
        const orbitDuration = 5 + (idx * 2);

        return (
          <React.Fragment key={idx}>
            {/* Moon orbit */}
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-900/40"
              style={{
                width: `${orbitRadius * 2}px`,
                height: `${orbitRadius * 2}px`
              }}
            />

            {/* Moon */}
            <motion.div
              className="absolute top-1/2 left-1/2"
              style={{
                width: `${moonSize}px`,
                height: `${moonSize}px`
              }}
              animate={{
                rotate: 360
              }}
              transition={{
                duration: orbitDuration,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <div
                className="rounded-full bg-gray-400"
                style={{
                  width: `${moonSize}px`,
                  height: `${moonSize}px`,
                  transform: `translateX(${orbitRadius}px)`,
                  boxShadow: '0 0 6px rgba(200, 200, 200, 0.6)'
                }}
              />
            </motion.div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default SolarSystemScene2D;
